/**
 * The running app's own version.
 *
 * These two values are injected at build time by Vite `define`
 * (__APP_VERSION_CODE__ / __APP_VERSION_NAME__), from the APP_VERSION_CODE /
 * APP_VERSION_NAME env vars CI sets. CI derives them ONCE per build and writes the
 * SAME numbers into android/app/build.gradle, so what the app thinks it is and
 * what the APK actually is can't drift. Locally (no env vars) they fall back to
 * dev defaults, which is fine because the updater is a no-op off-device anyway.
 *
 * The `typeof` guard means this is safe even in a context where the define didn't
 * run (e.g. a bare test import): an undeclared identifier under `typeof` yields
 * "undefined" rather than throwing, so we quietly use the fallback.
 */
import type { AppVersion } from './updateManifest';

const DEV_VERSION_CODE = 0;
const DEV_VERSION_NAME = 'dev';

const injectedCode =
  typeof __APP_VERSION_CODE__ !== 'undefined' ? __APP_VERSION_CODE__ : DEV_VERSION_CODE;
const injectedName =
  typeof __APP_VERSION_NAME__ !== 'undefined' ? __APP_VERSION_NAME__ : DEV_VERSION_NAME;

export const APP_VERSION: AppVersion = {
  versionCode: injectedCode,
  versionName: injectedName,
};

/**
 * The commit this build was made from, short form. 'local' off CI.
 *
 * Kept separate from APP_VERSION on purpose: that shape is what the updater
 * compares a manifest against, and a build identifier has no business in a
 * version comparison. This is for humans reading a bug report.
 *
 * It earns its place because the build NUMBER is a CI run counter and says
 * nothing at all about which code went in. Working out whether build 282 had a
 * given fix in it cost a full round trip and a wasted overnight test, and the
 * answer was no. The commit removes the guessing.
 */
export const APP_COMMIT: string =
  typeof __APP_VERSION_SHA__ !== 'undefined' ? __APP_VERSION_SHA__ : 'local';
